import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  adminCreateCategory,
  adminDeleteCategory,
  adminListCategories,
  adminUpdateCategory,
  type Category,
} from '../api';
import { CategoryIcon, CATEGORY_ICON_CHOICES, defaultCategoryIconId } from '../categoryIcons';

function IconPickerGrid({
  value,
  onPick,
}: {
  value: string;
  onPick: (iconId: string) => void;
}) {
  return (
    <div className="icon-picker-grid" role="list">
      {CATEGORY_ICON_CHOICES.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`icon-picker-cell ${value === c.id ? 'selected' : ''}`}
          onClick={() => onPick(c.id)}
          title={c.label}
          aria-label={c.label}
          aria-pressed={value === c.id}
        >
          <c.Icon size={22} strokeWidth={1.65} className="icon-picker-svg" aria-hidden />
        </button>
      ))}
    </div>
  );
}

/** Contenuto gestione categorie: usare solo se l’utente è già verificato come admin. */
export function AdminCategoriesContent() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [name, setName] = useState('');
  const [type, setType] = useState<'out' | 'in'>('out');
  const [newIcon, setNewIcon] = useState(() => defaultCategoryIconId());
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [iconEditFor, setIconEditFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const c = await adminListCategories();
      setCategories(c.categories);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setErr('');
    try {
      await adminCreateCategory({ name: name.trim(), type, icon: newIcon });
      setName('');
      setNewIcon(defaultCategoryIconId());
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    }
  }

  async function onDelete(id: string) {
    if (!confirm('Eliminare questa categoria globale?')) return;
    setErr('');
    try {
      await adminDeleteCategory(id);
      setIconEditFor(null);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    }
  }

  async function onRename(id: string, current: string) {
    const n = prompt('Nuovo nome', current);
    if (!n?.trim()) return;
    setErr('');
    try {
      await adminUpdateCategory(id, { name: n.trim() });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    }
  }

  async function onSetIcon(categoryId: string, iconId: string) {
    setErr('');
    try {
      await adminUpdateCategory(categoryId, { icon: iconId });
      setIconEditFor(null);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    }
  }

  if (loading) {
    return <p className="home-muted">Caricamento…</p>;
  }

  return (
    <div className="admin-cat-page">
      <h2 className="page-title">Categorie globali</h2>
      <p className="profile-hint">
        Da qui gestisci le categorie di sistema usate da tutti gli utenti. Scegli un’icona per ogni
        categoria.
      </p>
      {err && <p className="field-err">{err}</p>}

      <form className="profile-card" onSubmit={onAdd}>
        <h3 className="profile-section-title">Nuova categoria</h3>
        <p className="profile-hint">Icona</p>
        <IconPickerGrid value={newIcon} onPick={setNewIcon} />
        <div className="field-row" style={{ marginTop: '0.75rem' }}>
          <div className="field flex-1">
            <label htmlFor="ac-name">Nome</label>
            <input id="ac-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field w-type">
            <label htmlFor="ac-type">Tipo</label>
            <select id="ac-type" value={type} onChange={(e) => setType(e.target.value as 'in' | 'out')}>
              <option value="out">Uscita</option>
              <option value="in">Entrata</option>
            </select>
          </div>
        </div>
        <button className="btn-primary" type="submit">
          Crea
        </button>
      </form>

      <ul className="admin-cat-list">
        {categories.map((c) => (
          <li key={c.categoryId} className={`admin-cat-item ${iconEditFor === c.categoryId ? 'expanded' : ''}`}>
            <div className="admin-cat-item-top">
              <div className="admin-cat-main">
                <span className="admin-cat-emoji" title={c.name}>
                  <CategoryIcon iconId={c.icon} size={26} className="admin-cat-icon-svg" />
                </span>
                <div>
                  <strong>{c.name}</strong>
                  <span className="home-muted"> · {c.type === 'in' ? 'entrata' : 'uscita'}</span>
                </div>
              </div>
              <div className="admin-cat-actions">
                <button type="button" className="btn-text" onClick={() => onRename(c.categoryId, c.name)}>
                  Rinomina
                </button>
                <button
                  type="button"
                  className="btn-text"
                  onClick={() => setIconEditFor((x) => (x === c.categoryId ? null : c.categoryId))}
                >
                  Icona
                </button>
                <button type="button" className="btn-text-danger" onClick={() => onDelete(c.categoryId)}>
                  Elimina
                </button>
              </div>
            </div>
            {iconEditFor === c.categoryId && (
              <div className="admin-cat-icon-panel">
                <IconPickerGrid
                  value={
                    c.icon && CATEGORY_ICON_CHOICES.some((x) => x.id === c.icon)
                      ? c.icon
                      : defaultCategoryIconId()
                  }
                  onPick={(iconId) => void onSetIcon(c.categoryId, iconId)}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Vecchia rotta `/categorie`: la gestione è sulla home per gli admin. */
export default function AdminCategoriesPage() {
  return <Navigate to="/" replace />;
}
